// src/app/modules/socios/AgregarSocioPage.tsx
import React, {useEffect, useMemo, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {fetchWithAuth} from '../../core/utils/fetchWithAuth'
import {PageTitle} from '../../../_metronic/layout/core'
import {KTIcon} from '../../../_metronic/helpers'

// ============================================================================
// TIPOS
// ============================================================================

type GiroDisponible = {
  id: number
  nombre: string
  descripcion: string | null
  estado: boolean
}

type Categoria = {
  id: number
  nombre: string
  descripcion: string
}

type Departamento = {
  id: number
  nombre: string
}

type Localidad = {
  id: number
  nombre: string
  departamentoId: number
  codigoPostal: string
  codigoZona: string
  municipio: string | null
  geo: string | null
}

type FormData = {
  numeroSocio: string
  nombreFantasia: string
  razonSocial: string
  rut: string
  ci: string
  bps: string
  departamentoId: string
  localidadId: string
  calle: string
  numeroPuerta: string
  comentario_direccion: string
  telefono: string
  contactoNombre: string
  contactoTelefono: string
  contactoEmail: string
  categoria: string
  exentoIVA: boolean
  exentoIRAE: boolean
  esSocio: boolean
  zonaCobro: string
}

// ============================================================================
// COMPONENTES AUXILIARES
// ============================================================================

const FormSection: React.FC<{
  title: string
  icon?: string
  children: React.ReactNode
  className?: string
}> = ({title, icon, children, className = ''}) => (
  <div className={`card shadow-sm ${className}`}>
    <div className='card-header'>
      <h3 className='card-title align-items-start flex-column'>
        <span className='card-label fw-bold text-dark'>
          {icon && <KTIcon iconName={icon} className='fs-1 me-2' />}
          {title}
        </span>
      </h3>
    </div>
    <div className='card-body'>{children}</div>
  </div>
)

const FormField: React.FC<{
  label: string
  value: string
  onChange: (value: string) => void
  type?: string
  required?: boolean
  placeholder?: string
  icon?: string
  disabled?: boolean
  helperText?: string
  actionButton?: React.ReactNode
}> = ({
  label,
  value,
  onChange,
  type = 'text',
  required = false,
  placeholder = '',
  icon,
  disabled = false,
  helperText,
  actionButton,
}) => (
  <div className='mb-5'>
    <label className='form-label fw-bold text-gray-700'>
      {icon && <KTIcon iconName={icon} className='fs-5 me-2' />}
      {label}
      {required && <span className='text-danger ms-1'>*</span>}
    </label>
    <div className={actionButton ? 'input-group' : ''}>
      <input
        type={type}
        className='form-control form-control-lg'
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
      />
      {actionButton && actionButton}
    </div>
    {helperText && <div className='form-text'>{helperText}</div>}
  </div>
)

const GirosSelector: React.FC<{
  selectedGiros: number[]
  availableGiros: GiroDisponible[]
  loading: boolean
  onChange: (giroIds: number[]) => void
  onSearch: (query: string) => void
}> = ({selectedGiros, availableGiros, loading, onChange, onSearch}) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [showDropdown, setShowDropdown] = useState(false)
  // Cache de todos los giros que se han cargado para mantener la referencia a los seleccionados
  const [girosCache, setGirosCache] = useState<Map<number, GiroDisponible>>(new Map())

  // Actualizar el cache con los nuevos giros disponibles
  useEffect(() => {
    setGirosCache((prevCache) => {
      const newCache = new Map(prevCache)
      availableGiros.forEach((giro) => {
        if (!newCache.has(giro.id)) {
          newCache.set(giro.id, giro)
        }
      })
      return newCache
    })
  }, [availableGiros])

  const filteredGiros = useMemo(() => {
    if (!searchQuery) return availableGiros
    return availableGiros.filter((g) =>
      g.nombre.toLowerCase().includes(searchQuery.toLowerCase())
    )
  }, [availableGiros, searchQuery])

  const handleAddGiro = (giroId: number) => {
    if (!selectedGiros.includes(giroId)) {
      onChange([...selectedGiros, giroId])
    }
    setSearchQuery('')
    setShowDropdown(false)
  }

  const handleRemoveGiro = (giroId: number) => {
    onChange(selectedGiros.filter((id) => id !== giroId))
  }

  // Usar el cache para obtener los giros seleccionados, así siempre se muestran
  const selectedGirosData = selectedGiros
    .map((id) => girosCache.get(id))
    .filter((giro): giro is GiroDisponible => giro !== undefined)

  return (
    <div className='mb-5'>
      <label className='form-label fw-bold text-gray-700'>
        <KTIcon iconName='briefcase' className='fs-5 me-2' />
        Giros Comerciales
        <span className='text-danger ms-1'>*</span>
      </label>

      {/* Giros Seleccionados */}
      <div className='d-flex flex-wrap gap-2 mb-3 p-3 bg-light rounded'>
        {selectedGirosData.length > 0 ? (
          selectedGirosData.map((giro) => (
            <span key={giro.id} className='badge badge-primary badge-lg'>
              <span className='me-2'>{giro.nombre}</span>
              <button
                type='button'
                className='btn btn-icon btn-sm btn-active-light-primary p-0'
                onClick={() => handleRemoveGiro(giro.id)}
                style={{width: '16px', height: '16px'}}
              >
                <KTIcon iconName='cross' className='fs-5' />
              </button>
            </span>
          ))
        ) : (
          <span className='text-muted fs-7'>No hay giros seleccionados</span>
        )}
      </div>

      {/* Buscador de Giros */}
      <div className='position-relative'>
        <input
          type='text'
          className='form-control form-control-lg'
          placeholder='Buscar giro para agregar...'
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value)
            onSearch(e.target.value)
            setShowDropdown(true)
          }}
          onFocus={() => setShowDropdown(true)}
          onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
        />

        {/* Dropdown de Resultados */}
        {showDropdown && searchQuery && (
          <div
            className='dropdown-menu show w-100 p-2'
            style={{
              maxHeight: '300px',
              overflowY: 'auto',
              zIndex: 1050,
            }}
          >
            {loading ? (
              <div className='text-center py-3'>
                <div className='spinner-border spinner-border-sm text-primary' />
                <div className='text-muted fs-7 mt-2'>Buscando giros...</div>
              </div>
            ) : filteredGiros.length > 0 ? (
              filteredGiros.map((giro) => {
                const isSelected = selectedGiros.includes(giro.id)
                return (
                  <button
                    key={giro.id}
                    type='button'
                    className={`dropdown-item rounded d-flex align-items-center justify-content-between ${
                      isSelected ? 'active' : ''
                    }`}
                    onClick={() => !isSelected && handleAddGiro(giro.id)}
                    disabled={isSelected}
                  >
                    <span>{giro.nombre}</span>
                    {isSelected && <KTIcon iconName='check' className='fs-3 text-success' />}
                  </button>
                )
              })
            ) : (
              <div className='text-center py-3'>
                <KTIcon iconName='file-deleted' className='fs-2x text-gray-400 mb-2' />
                <div className='text-muted fs-7'>No se encontraron giros</div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className='form-text'>
        Busca y selecciona los giros comerciales del socio. Los giros seleccionados aparecerán
        arriba.
      </div>
    </div>
  )
}

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

function AgregarSocioPage() {
  const navigate = useNavigate()

  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [duplicateError, setDuplicateError] = useState<{
    message: string
    idEmpresa: number
    rut: string
  } | null>(null)

  // Estados para datos de la API
  const [girosDisponibles, setGirosDisponibles] = useState<GiroDisponible[]>([])
  const [loadingGiros, setLoadingGiros] = useState<boolean>(false)
  const [selectedGiroIds, setSelectedGiroIds] = useState<number[]>([])

  const [categorias, setCategorias] = useState<Categoria[]>([])
  const [loadingCategorias, setLoadingCategorias] = useState<boolean>(false)

  const [departamentos, setDepartamentos] = useState<Departamento[]>([])
  const [loadingDepartamentos, setLoadingDepartamentos] = useState<boolean>(false)
  const [localidades, setLocalidades] = useState<Localidad[]>([])
  const [localidadesFiltradas, setLocalidadesFiltradas] = useState<Localidad[]>([])
  const [loadingLocalidades, setLoadingLocalidades] = useState<boolean>(false)
  const [busquedaLocalidad, setBusquedaLocalidad] = useState<string>('')

  // Estado para la imagen
  const [imagenPreview, setImagenPreview] = useState<string | null>(null)
  const [imagenBase64, setImagenBase64] = useState<string | null>(null)
  const [imagenNombre, setImagenNombre] = useState<string>('')

  // Estados para validación
  const [validationModal, setValidationModal] = useState<{
    show: boolean
    type: 'rut' | 'numeroSocio' | null
    exists: boolean
    nombre: string
    value: string
  }>({
    show: false,
    type: null,
    exists: false,
    nombre: '',
    value: '',
  })
  const [validating, setValidating] = useState<boolean>(false)

  // Estado del formulario
  const [formData, setFormData] = useState<FormData>({
    numeroSocio: '',
    nombreFantasia: '',
    razonSocial: '',
    rut: '',
    ci: '',
    bps: '',
    departamentoId: '',
    localidadId: '',
    calle: '',
    numeroPuerta: '',
    comentario_direccion: '',
    telefono: '',
    contactoNombre: '',
    contactoTelefono: '',
    contactoEmail: '',
    categoria: '',
    exentoIVA: false,
    exentoIRAE: false,
    esSocio: true,
    zonaCobro: '60',
  })

  const getToken = (): string | null => localStorage.getItem('X-TOKEN')

  // Fetch Giros
  const fetchGiros = async (query?: string) => {
    try {
      setLoadingGiros(true)
      const token = getToken()
      if (!token) throw new Error('No se encontró el token de autenticación')

      const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-TOKEN': token,
        },
        body: JSON.stringify({
          service: 'getGiros',
          activo: '1',
          ...(query && query.length > 0 ? {q: query} : {}),
        }),
      })

      if (!response.ok) throw new Error(`Error en la petición: ${response.status}`)
      const data = await response.json()

      if (data.code === 'OK') {
        setGirosDisponibles(data.data.giros || [])
      } else if (data.code === 'ERROR' && data.returned_message?.includes('JWT expired')) {
        localStorage.removeItem('X-TOKEN')
        navigate('/auth/login')
      }
    } catch (err) {
      console.error('Error al cargar giros:', err)
    } finally {
      setLoadingGiros(false)
    }
  }

  // Fetch Categorias
  const fetchCategorias = async () => {
    try {
      setLoadingCategorias(true)
      const token = getToken()
      if (!token) throw new Error('No se encontró el token de autenticación')

      const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-TOKEN': token,
        },
        body: JSON.stringify({
          service: 'getCategorias',
        }),
      })

      if (!response.ok) throw new Error(`Error en la petición: ${response.status}`)
      const data = await response.json()

      if (data.code === 'OK') {
        setCategorias(data.data.categorias || [])
      } else if (data.code === 'ERROR' && data.returned_message?.includes('JWT expired')) {
        localStorage.removeItem('X-TOKEN')
        navigate('/auth/login')
      }
    } catch (err) {
      console.error('Error al cargar categorías:', err)
    } finally {
      setLoadingCategorias(false)
    }
  }

  // Fetch Departamentos
  const fetchDepartamentos = async () => {
    try {
      setLoadingDepartamentos(true)
      const token = getToken()
      if (!token) throw new Error('No se encontró el token de autenticación')

      const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-TOKEN': token,
        },
        body: JSON.stringify({
          service: 'getDepartamentos',
        }),
      })

      if (!response.ok) throw new Error(`Error en la petición: ${response.status}`)
      const data = await response.json()

      if (data.code === 'OK') {
        setDepartamentos(data.data || [])
      } else if (data.code === 'ERROR' && data.returned_message?.includes('JWT expired')) {
        localStorage.removeItem('X-TOKEN')
        navigate('/auth/login')
      }
    } catch (err) {
      console.error('Error al cargar departamentos:', err)
    } finally {
      setLoadingDepartamentos(false)
    }
  }

  // Fetch Localidades
  const fetchLocalidades = async () => {
    try {
      setLoadingLocalidades(true)
      const token = getToken()
      if (!token) throw new Error('No se encontró el token de autenticación')

      const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-TOKEN': token,
        },
        body: JSON.stringify({
          service: 'getLocalidades',
          includeDepartamento: false,
        }),
      })

      if (!response.ok) throw new Error(`Error en la petición: ${response.status}`)
      const data = await response.json()

      if (data.code === 'OK') {
        setLocalidades(data.data || [])
      } else if (data.code === 'ERROR' && data.returned_message?.includes('JWT expired')) {
        localStorage.removeItem('X-TOKEN')
        navigate('/auth/login')
      }
    } catch (err) {
      console.error('Error al cargar localidades:', err)
    } finally {
      setLoadingLocalidades(false)
    }
  }

  // Filtrar localidades por departamento y búsqueda
  useEffect(() => {
    if (!formData.departamentoId) {
      setLocalidadesFiltradas([])
      setFormData((prev) => ({...prev, localidadId: ''}))
      return
    }

    const departamentoId = parseInt(formData.departamentoId)
    let filtradas = localidades.filter((loc) => loc.departamentoId === departamentoId)

    // Aplicar filtro de búsqueda si existe
    if (busquedaLocalidad.trim()) {
      const busqueda = busquedaLocalidad.toLowerCase()
      filtradas = filtradas.filter(
        (loc) =>
          loc.nombre.toLowerCase().includes(busqueda) ||
          loc.codigoPostal.includes(busqueda)
      )
    }

    setLocalidadesFiltradas(filtradas)

    // Si la localidad seleccionada no pertenece al departamento actual, limpiarla
    if (formData.localidadId) {
      const localidadSeleccionada = localidades.find(
        (loc) => loc.id.toString() === formData.localidadId
      )
      if (
        localidadSeleccionada &&
        localidadSeleccionada.departamentoId !== departamentoId
      ) {
        setFormData((prev) => ({...prev, localidadId: ''}))
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.departamentoId, busquedaLocalidad, localidades])

  // Manejar cambio de departamento
  const handleDepartamentoChange = (departamentoId: string) => {
    setFormData({
      ...formData,
      departamentoId,
      localidadId: '', // Limpiar localidad al cambiar departamento
    })
    setBusquedaLocalidad('') // Limpiar búsqueda
  }

  // Manejar subida de imagen
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validar que sea una imagen
    if (!file.type.startsWith('image/')) {
      setError('El archivo debe ser una imagen')
      return
    }

    // Validar tamaño (máximo 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('La imagen no debe superar los 5MB')
      return
    }

    const reader = new FileReader()
    reader.onloadend = () => {
      const base64String = reader.result as string
      // Remover el prefijo data:image/...;base64,
      const base64Data = base64String.split(',')[1]
      setImagenBase64(base64Data)
      setImagenPreview(base64String)
      setImagenNombre(file.name)
      setError(null)
    }
    reader.onerror = () => {
      setError('Error al leer el archivo')
    }
    reader.readAsDataURL(file)
  }

  // Cargar datos al montar
  useEffect(() => {
    fetchGiros()
    fetchCategorias()
    fetchDepartamentos()
    fetchLocalidades()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Validar RUT
  const handleValidateRUT = async () => {
    if (!formData.rut.trim()) {
      setError('Debe ingresar un RUT para validar')
      return
    }

    try {
      setValidating(true)
      setError(null)
      const token = getToken()
      if (!token) throw new Error('No se encontró el token de autenticación')

      const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-TOKEN': token,
        },
        body: JSON.stringify({
          service: 'existeMiembro',
          nroSocio: '',
          idMiembro: '',
          rut: formData.rut,
        }),
      })

      if (!response.ok) throw new Error(`Error en la petición: ${response.status}`)
      const data = await response.json()

      if (data.code === 'OK') {
        const exists = data.data.nombre && data.data.nombre.trim() !== ''
        setValidationModal({
          show: true,
          type: 'rut',
          exists,
          nombre: data.data.nombre || '',
          value: formData.rut,
        })
      } else if (data.code === 'ERROR' && data.returned_message?.includes('JWT expired')) {
        localStorage.removeItem('X-TOKEN')
        navigate('/auth/login')
      } else {
        throw new Error(data.returned_message || 'Error al validar RUT')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al validar RUT')
    } finally {
      setValidating(false)
    }
  }

  // Verificar disponibilidad de número de socio
  const handleVerifyNumeroSocio = async () => {
    if (!formData.numeroSocio.trim()) {
      setError('Debe ingresar un número de socio para verificar')
      return
    }

    try {
      setValidating(true)
      setError(null)
      const token = getToken()
      if (!token) throw new Error('No se encontró el token de autenticación')

      const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-TOKEN': token,
        },
        body: JSON.stringify({
          service: 'existeMiembro',
          nroSocio: formData.numeroSocio,
          idMiembro: '',
          rut: '',
        }),
      })

      if (!response.ok) throw new Error(`Error en la petición: ${response.status}`)
      const data = await response.json()

      if (data.code === 'OK') {
        const exists = data.data.nombre && data.data.nombre.trim() !== ''
        setValidationModal({
          show: true,
          type: 'numeroSocio',
          exists,
          nombre: data.data.nombre || '',
          value: formData.numeroSocio,
        })
      } else if (data.code === 'ERROR' && data.returned_message?.includes('JWT expired')) {
        localStorage.removeItem('X-TOKEN')
        navigate('/auth/login')
      } else {
        throw new Error(data.returned_message || 'Error al verificar número de socio')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al verificar número de socio')
    } finally {
      setValidating(false)
    }
  }

  // Handle Submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      setLoading(true)
      setError(null)
      setDuplicateError(null)

      const token = getToken()
      if (!token) throw new Error('No se encontró el token de autenticación')

      // Validar que se haya seleccionado departamento y localidad
      if (!formData.departamentoId || !formData.localidadId) {
        setError('Debe seleccionar un departamento y una localidad')
        setLoading(false)
        return
      }

      // Obtener nombres de departamento y localidad
      const departamentoSeleccionado = departamentos.find(
        (d) => d.id.toString() === formData.departamentoId
      )
      const localidadSeleccionada = localidades.find(
        (l) => l.id.toString() === formData.localidadId
      )

      if (!departamentoSeleccionado || !localidadSeleccionada) {
        setError('Error al obtener los datos de departamento o localidad')
        setLoading(false)
        return
      }

      // Determinar el tipo de contenido de la imagen
      const getImageContentType = (filename: string): string => {
        const ext = filename.split('.').pop()?.toLowerCase()
        switch (ext) {
          case 'jpg':
          case 'jpeg':
            return 'image/jpeg'
          case 'png':
            return 'image/png'
          case 'gif':
            return 'image/gif'
          default:
            return 'image/jpeg'
        }
      }

      // Construir attachments si hay imagen
      const attachments = imagenBase64
        ? [
            {
              purpose: 'MIEMBRO_FOTO',
              filename: imagenNombre || 'foto.jpg',
              contentType: getImageContentType(imagenNombre || 'foto.jpg'),
              dataBase64: imagenBase64,
            },
          ]
        : []

      const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-TOKEN': token,
        },
        body: JSON.stringify({
          service: 'createMiembro',
          clase: 'Empresa',
          activa: 'Activa',
          esSocio: formData.esSocio,
          categoria: formData.categoria,
          zonaCobro: parseInt(formData.zonaCobro),
          nombreFantasia: formData.nombreFantasia,
          razonSocial: formData.razonSocial,
          rut: formData.rut,
          telefono: formData.telefono,
          email: formData.contactoEmail, // El usuario mencionó que este parámetro hoy no viaja, pero lo dejamos por si acaso
          ciudad: localidadSeleccionada.nombre,
          departamento: departamentoSeleccionado.nombre,
          calle: formData.calle,
          numeroPuerta: formData.numeroPuerta,
          comentario_direccion: formData.comentario_direccion || '',
          contactoNombre: formData.contactoNombre,
          contactoTelefono: formData.contactoTelefono,
          contactoEmail: formData.contactoEmail,
          ci: formData.ci,
          observaciones: '',
          attachments: attachments,
          ...(formData.numeroSocio ? {numeroSocio: formData.numeroSocio} : {}),
        }),
      })

      if (!response.ok) throw new Error(`Error en la petición: ${response.status}`)
      const data = await response.json()

      if (data.code === 'OK') {
        // Éxito - Mostrar toast y redirigir
        const toast = document.createElement('div')
        toast.className =
          'toast align-items-center text-bg-success border-0 position-fixed top-0 end-0 m-3'
        toast.style.zIndex = '9999'
        toast.innerHTML = `
          <div class="d-flex">
            <div class="toast-body">
              <i class="bi bi-check-circle me-2"></i>
              Socio creado exitosamente - ID: ${data.data.idMiembro}
            </div>
            <button type="button" class="btn-close btn-close-white me-2 m-auto" data-bs-dismiss="toast"></button>
          </div>
        `
        document.body.appendChild(toast)
        setTimeout(() => {
          toast.remove()
          navigate(`/socios/${data.data.idMiembro}`)
        }, 2000)
      } else if (data.code === 'DUPLICATE') {
        // Duplicado
        setDuplicateError({
          message: data.msg || 'Ya existe un socio con estos datos',
          idEmpresa: data.idEmpresa,
          rut: data.rut,
        })
      } else if (data.code === 'ERROR' && data.returned_message?.includes('JWT expired')) {
        localStorage.removeItem('X-TOKEN')
        navigate('/auth/login')
        return
      } else {
        throw new Error(data.returned_message || 'Error al crear el socio')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido')
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <PageTitle>Agregar Nuevo Socio</PageTitle>

      {/* Mensajes de Error */}
      {error && (
        <div className='alert alert-danger d-flex align-items-center p-5 mb-5'>
          <KTIcon iconName='information-5' className='fs-2hx text-danger me-4' />
          <div className='d-flex flex-column'>
            <h4 className='mb-1 text-danger'>Error</h4>
            <span>{error}</span>
          </div>
        </div>
      )}

      {duplicateError && (
        <div className='alert alert-warning d-flex align-items-center p-5 mb-5'>
          <KTIcon iconName='information-5' className='fs-2hx text-warning me-4' />
          <div className='d-flex flex-column flex-grow-1'>
            <h4 className='mb-1 text-warning'>Socio Duplicado</h4>
            <span>{duplicateError.message}</span>
            <div className='mt-3'>
              <strong>RUT:</strong> {duplicateError.rut}
              <br />
              <strong>ID Empresa:</strong> {duplicateError.idEmpresa}
            </div>
          </div>
          <button
            className='btn btn-sm btn-warning'
            onClick={() => navigate(`/socios/${duplicateError.idEmpresa}`)}
          >
            Ver Socio Existente
          </button>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className='row g-5'>
          {/* Columna Izquierda */}
          <div className='col-xl-6'>
            {/* Información Básica */}
            <FormSection title='Información Básica' icon='profile-circle' className='mb-5'>
              <FormField
                label='Número de Socio'
                value={formData.numeroSocio}
                onChange={(v) => setFormData({...formData, numeroSocio: v})}
                icon='barcode'
                placeholder='Dejar vacío para asignar automáticamente'
                helperText='Opcional: Especifica un número de socio. Si está vacío, se asignará automáticamente.'
                actionButton={
                  <button
                    type='button'
                    className='btn btn-secondary'
                    onClick={handleVerifyNumeroSocio}
                    disabled={!formData.numeroSocio || validating}
                  >
                    {validating ? (
                      <>
                        <span className='spinner-border spinner-border-sm me-2' />
                        Verificando...
                      </>
                    ) : (
                      <>
                        <KTIcon iconName='verify' className='fs-3' />
                        Verificar
                      </>
                    )}
                  </button>
                }
              />

              <FormField
                label='Nombre Fantasía'
                value={formData.nombreFantasia}
                onChange={(v) => setFormData({...formData, nombreFantasia: v})}
                icon='abstract-26'
                required
                placeholder='Ingresa el nombre fantasía'
              />

              <FormField
                label='Razón Social'
                value={formData.razonSocial}
                onChange={(v) => setFormData({...formData, razonSocial: v})}
                icon='office-bag'
                required
                placeholder='Ingresa la razón social'
              />

              <FormField
                label='RUT'
                value={formData.rut}
                onChange={(v) => setFormData({...formData, rut: v})}
                icon='document'
                required
                placeholder='Ej: 150812940019'
                actionButton={
                  <button
                    type='button'
                    className='btn btn-secondary'
                    onClick={handleValidateRUT}
                    disabled={!formData.rut || validating}
                  >
                    {validating ? (
                      <>
                        <span className='spinner-border spinner-border-sm me-2' />
                        Validando...
                      </>
                    ) : (
                      <>
                        <KTIcon iconName='verify' className='fs-3' />
                        Validar
                      </>
                    )}
                  </button>
                }
              />

              <FormField
                label='CI (Cédula de Identidad)'
                value={formData.ci}
                onChange={(v) => setFormData({...formData, ci: v})}
                icon='profile-user'
                placeholder='Opcional'
              />

              <FormField
                label='BPS (UREFI)'
                value={formData.bps}
                onChange={(v) => setFormData({...formData, bps: v})}
                icon='shield-tick'
                placeholder='Número de BPS'
              />

              <FormField
                label='Teléfono'
                value={formData.telefono}
                onChange={(v) => setFormData({...formData, telefono: v})}
                icon='phone'
                type='tel'
                placeholder='Ej: 092427260'
              />
            </FormSection>

            {/* Ubicación */}
            <FormSection title='Ubicación' icon='geolocation' className='mb-5'>
              {/* Departamento */}
              <div className='mb-5'>
                <label className='form-label fw-bold text-gray-700'>
                  <KTIcon iconName='geolocation' className='fs-5 me-2' />
                  Departamento
                  <span className='text-danger ms-1'>*</span>
                </label>
                {loadingDepartamentos ? (
                  <div className='text-center py-3'>
                    <div className='spinner-border spinner-border-sm text-primary' />
                  </div>
                ) : (
                  <select
                    className='form-select form-select-lg'
                    value={formData.departamentoId}
                    onChange={(e) => handleDepartamentoChange(e.target.value)}
                    required
                  >
                    <option value=''>Seleccionar departamento...</option>
                    {departamentos.map((dept) => (
                      <option key={dept.id} value={dept.id.toString()}>
                        {dept.nombre}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Localidad */}
              <div className='mb-5'>
                <label className='form-label fw-bold text-gray-700'>
                  <KTIcon iconName='geolocation' className='fs-5 me-2' />
                  Localidad
                  <span className='text-danger ms-1'>*</span>
                </label>
                {!formData.departamentoId ? (
                  <div className='alert alert-info'>
                    <KTIcon iconName='information-5' className='fs-5 me-2' />
                    Debe seleccionar un departamento primero
                  </div>
                ) : loadingLocalidades ? (
                  <div className='text-center py-3'>
                    <div className='spinner-border spinner-border-sm text-primary' />
                  </div>
                ) : (
                  <>
                    {/* Campo de búsqueda */}
                    <div className='mb-3'>
                      <div className='position-relative'>
                        <KTIcon
                          iconName='magnifier'
                          className='fs-2 position-absolute top-50 start-0 translate-middle-y ms-4'
                        />
                        <input
                          type='text'
                          className='form-control form-control-lg ps-14'
                          placeholder='Buscar localidad por nombre o código postal...'
                          value={busquedaLocalidad}
                          onChange={(e) => setBusquedaLocalidad(e.target.value)}
                        />
                      </div>
                    </div>

                    {/* Selector de localidad */}
                    <select
                      className='form-select form-select-lg'
                      value={formData.localidadId}
                      onChange={(e) =>
                        setFormData({...formData, localidadId: e.target.value})
                      }
                      required
                      disabled={localidadesFiltradas.length === 0}
                    >
                      <option value=''>
                        {localidadesFiltradas.length === 0
                          ? 'No hay localidades disponibles'
                          : 'Seleccionar localidad...'}
                      </option>
                      {localidadesFiltradas.map((loc) => (
                        <option key={loc.id} value={loc.id.toString()}>
                          {loc.nombre}
                          {loc.codigoPostal && loc.codigoPostal !== 'N/A'
                            ? ` (${loc.codigoPostal})`
                            : ''}
                        </option>
                      ))}
                    </select>
                    {localidadesFiltradas.length > 0 && (
                      <div className='form-text'>
                        {localidadesFiltradas.length} localidad
                        {localidadesFiltradas.length !== 1 ? 'es' : ''} encontrada
                        {localidadesFiltradas.length !== 1 ? 's' : ''}
                      </div>
                    )}
                  </>
                )}
              </div>

              <FormField
                label='Calle'
                value={formData.calle}
                onChange={(v) => setFormData({...formData, calle: v})}
                icon='route'
                placeholder='Ej: Av. 18 de Julio'
              />

              <FormField
                label='Número de Puerta'
                value={formData.numeroPuerta}
                onChange={(v) => setFormData({...formData, numeroPuerta: v})}
                icon='home'
                placeholder='Ej: 1234'
              />

              <FormField
                label='Comentario de Dirección'
                value={formData.comentario_direccion}
                onChange={(v) => setFormData({...formData, comentario_direccion: v})}
                icon='information-5'
                placeholder='Ej: Frente a la plaza'
              />
            </FormSection>
          </div>

          {/* Columna Derecha */}
          <div className='col-xl-6'>
            {/* Información de Contacto */}
            <FormSection title='Información de Contacto' icon='sms' className='mb-5'>
              <FormField
                label='Nombre de Contacto'
                value={formData.contactoNombre}
                onChange={(v) => setFormData({...formData, contactoNombre: v})}
                icon='user'
                required
                placeholder='Nombre completo'
              />

              <FormField
                label='Teléfono de Contacto'
                value={formData.contactoTelefono}
                onChange={(v) => setFormData({...formData, contactoTelefono: v})}
                icon='phone'
                type='tel'
                required
                placeholder='Ej: 092427260'
              />

              <FormField
                label='Email de Contacto'
                value={formData.contactoEmail}
                onChange={(v) => setFormData({...formData, contactoEmail: v})}
                icon='sms'
                type='email'
                required
                placeholder='ejemplo@email.com'
              />
            </FormSection>

            {/* Foto del Socio */}
            <FormSection title='Foto del Socio' icon='picture' className='mb-5'>
              <div className='mb-5'>
                <label className='form-label fw-bold text-gray-700'>
                  <KTIcon iconName='picture' className='fs-5 me-2' />
                  Imagen de Perfil
                </label>

                {/* Preview de imagen */}
                {imagenPreview && (
                  <div className='mb-3'>
                    <div className='d-flex align-items-center gap-3 p-3 bg-light rounded'>
                      <img
                        src={imagenPreview}
                        alt='Preview'
                        style={{
                          width: '100px',
                          height: '100px',
                          objectFit: 'cover',
                          borderRadius: '8px',
                        }}
                      />
                      <div className='flex-grow-1'>
                        <div className='fw-bold text-gray-800'>{imagenNombre}</div>
                        <div className='text-muted fs-7'>
                          Imagen lista para subir
                        </div>
                      </div>
                      <button
                        type='button'
                        className='btn btn-sm btn-light-danger'
                        onClick={() => {
                          setImagenPreview(null)
                          setImagenBase64(null)
                          setImagenNombre('')
                        }}
                      >
                        <KTIcon iconName='cross' className='fs-5' />
                      </button>
                    </div>
                  </div>
                )}

                {/* Input de archivo */}
                <div
                  className={`d-flex align-items-center justify-content-center bg-light-secondary rounded p-8 border border-dashed ${
                    imagenPreview ? 'border-success' : 'border-secondary'
                  }`}
                  style={{cursor: 'pointer'}}
                  onClick={() => document.getElementById('file-input')?.click()}
                >
                  <div className='text-center'>
                    <KTIcon
                      iconName={imagenPreview ? 'check-circle' : 'file-up'}
                      className={`fs-3x mb-3 ${imagenPreview ? 'text-success' : 'text-secondary'}`}
                    />
                    <div className='text-gray-600 fw-bold fs-6 mb-2'>
                      {imagenPreview
                        ? 'Imagen seleccionada'
                        : 'Haz clic para seleccionar una imagen'}
                    </div>
                    <div className='text-muted fs-7'>
                      {imagenPreview
                        ? 'Haz clic para cambiar la imagen'
                        : 'Formatos: JPG, PNG, GIF (máx. 5MB)'}
                    </div>
                  </div>
                </div>

                <input
                  id='file-input'
                  type='file'
                  className='form-control form-control-lg mt-3'
                  accept='image/*'
                  onChange={handleImageUpload}
                  style={{display: 'none'}}
                />

                <div className='form-text mt-2'>
                  <KTIcon iconName='information-5' className='fs-5 me-1' />
                  La imagen se convertirá automáticamente a base64 para su envío
                </div>
              </div>
            </FormSection>

            {/* Categoría y Configuración */}
            <FormSection title='Categoría y Configuración' icon='setting-2' className='mb-5'>
              <div className='mb-5'>
                <label className='form-label fw-bold text-gray-700'>
                  <KTIcon iconName='category' className='fs-5 me-2' />
                  Tipo de Socio (Categoría)
                  <span className='text-danger ms-1'>*</span>
                </label>
                {loadingCategorias ? (
                  <div className='text-center py-3'>
                    <div className='spinner-border spinner-border-sm text-primary' />
                  </div>
                ) : (
                  <select
                    className='form-select form-select-lg'
                    value={formData.categoria}
                    onChange={(e) => setFormData({...formData, categoria: e.target.value})}
                    required
                  >
                    <option value=''>Seleccionar categoría...</option>
                    {categorias.map((cat) => (
                      <option key={cat.id} value={cat.nombre}>
                        {cat.nombre} - {cat.descripcion}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <FormField
                label='Zona de Cobro'
                value={formData.zonaCobro}
                onChange={(v) => setFormData({...formData, zonaCobro: v})}
                icon='geolocation-home'
                required
                placeholder='Ej: 60'
              />

              {/* Checkboxes de Exención */}
              <div className='mb-5'>
                <label className='form-label fw-bold text-gray-700 mb-3'>
                  <KTIcon iconName='security-user' className='fs-5 me-2' />
                  Exenciones Tributarias
                </label>

                <div className='form-check form-check-custom form-check-solid mb-3'>
                  <input
                    className='form-check-input'
                    type='checkbox'
                    id='exentoIVA'
                    checked={formData.exentoIVA}
                    onChange={(e) => setFormData({...formData, exentoIVA: e.target.checked})}
                  />
                  <label className='form-check-label fw-semibold' htmlFor='exentoIVA'>
                    Exento de IVA
                  </label>
                </div>

                <div className='form-check form-check-custom form-check-solid'>
                  <input
                    className='form-check-input'
                    type='checkbox'
                    id='exentoIRAE'
                    checked={formData.exentoIRAE}
                    onChange={(e) => setFormData({...formData, exentoIRAE: e.target.checked})}
                  />
                  <label className='form-check-label fw-semibold' htmlFor='exentoIRAE'>
                    Exento de IRAE
                  </label>
                </div>
              </div>

              {/* Toggle Es Socio */}
              <div className='mb-5'>
                <div className='form-check form-switch form-check-custom form-check-solid'>
                  <input
                    className='form-check-input'
                    type='checkbox'
                    id='esSocio'
                    checked={formData.esSocio}
                    onChange={(e) => setFormData({...formData, esSocio: e.target.checked})}
                  />
                  <label className='form-check-label fw-bold text-gray-700' htmlFor='esSocio'>
                    Es Socio
                  </label>
                </div>
                <div className='form-text'>
                  Marcar si este miembro es un socio activo de la organización
                </div>
              </div>
            </FormSection>

            {/* Giros Comerciales */}
            <FormSection title='Giros Comerciales' icon='briefcase'>
              <GirosSelector
                selectedGiros={selectedGiroIds}
                availableGiros={girosDisponibles}
                loading={loadingGiros}
                onChange={(giroIds) => setSelectedGiroIds(giroIds)}
                onSearch={(query) => fetchGiros(query)}
              />
            </FormSection>
          </div>
        </div>

        {/* Botones de Acción */}
        <div className='card shadow-sm mt-5'>
          <div className='card-body'>
            <div className='d-flex justify-content-end gap-3'>
              <button
                type='button'
                className='btn btn-light btn-lg'
                disabled={loading}
                onClick={() => navigate('/socios')}
              >
                <KTIcon iconName='cross' className='fs-3' />
                Cancelar
              </button>
              <button type='submit' className='btn btn-primary btn-lg' disabled={loading}>
                {loading ? (
                  <>
                    <span className='spinner-border spinner-border-sm me-2' />
                    Guardando...
                  </>
                ) : (
                  <>
                    <KTIcon iconName='check' className='fs-3' />
                    Guardar Socio
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </form>

      {/* Modal de Validación */}
      {validationModal.show && (
        <>
          <div
            className='modal-backdrop fade show'
            style={{zIndex: 1050}}
            onClick={() =>
              setValidationModal({show: false, type: null, exists: false, nombre: '', value: ''})
            }
          />
          <div
            className='modal fade show d-block'
            tabIndex={-1}
            role='dialog'
            aria-modal='true'
            style={{zIndex: 1055, position: 'fixed', inset: 0, pointerEvents: 'none'}}
          >
            <div className='modal-dialog modal-dialog-centered' role='document' style={{pointerEvents: 'auto'}}>
              <div className='modal-content'>
                <div className='modal-header'>
                  <h5 className='modal-title'>
                    {validationModal.type === 'rut' ? 'Validación de RUT' : 'Verificación de Número de Socio'}
                  </h5>
                  <button
                    type='button'
                    className='btn-close'
                    aria-label='Close'
                    onClick={() =>
                      setValidationModal({show: false, type: null, exists: false, nombre: '', value: ''})
                    }
                  />
                </div>
                <div className='modal-body'>
                  {validationModal.exists ? (
                    <div className='alert alert-warning d-flex align-items-center p-5'>
                      <KTIcon iconName='information-5' className='fs-2hx text-warning me-4' />
                      <div className='d-flex flex-column'>
                        <h4 className='mb-2 text-warning'>
                          {validationModal.type === 'rut' ? 'RUT' : 'Número de Socio'} ya existe
                        </h4>
                        <div className='mb-2'>
                          <strong>
                            {validationModal.type === 'rut' ? 'RUT' : 'Número de Socio'}:
                          </strong>{' '}
                          {validationModal.value}
                        </div>
                        <div>
                          <strong>Socio:</strong> {validationModal.nombre}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className='alert alert-success d-flex align-items-center p-5'>
                      <KTIcon iconName='check-circle' className='fs-2hx text-success me-4' />
                      <div className='d-flex flex-column'>
                        <h4 className='mb-2 text-success'>
                          {validationModal.type === 'rut' ? 'RUT' : 'Número de Socio'} disponible
                        </h4>
                        <div>
                          El {validationModal.type === 'rut' ? 'RUT' : 'número de socio'}{' '}
                          <strong>{validationModal.value}</strong> está disponible y puede ser utilizado.
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className='modal-footer'>
                  <button
                    className='btn btn-primary'
                    onClick={() =>
                      setValidationModal({show: false, type: null, exists: false, nombre: '', value: ''})
                    }
                  >
                    Aceptar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  )
}

export default AgregarSocioPage
